import isNil from 'lodash-es/isNil';
import { getPageQueryParams } from './pagination';
/** Used to group under the same cache key a requester request that make use of other requests */
const cacheKeys = {
    getAllPages: '~getAllPages'
};
/** TODO: extract getMany method */
export const get = ({ rootPath, fromRaw, requesterBuilder, params, cacheConfig, ...ctxParams }) => (resourceId) => requesterBuilder({
    path: `${rootPath}${typeof resourceId !== 'undefined' ? `/${resourceId}` : ''}`,
    requestCtx: {
        transform: fromRaw,
        params,
        ...ctxParams
    },
    config: cacheConfig
});
export const getPage = ({ getMany, query: baseQuery }) => (page, query) => {
    const queryWithoutPaginationParams = {
        ...(baseQuery || {}),
        ...(query || {})
    };
    return getMany({
        ...queryWithoutPaginationParams,
        ...getPageQueryParams(page, query?.limit || 100)
    });
};
/** Get the first page (limit: 100), then grab remaining pages in parallel and concat every page results in a single array */
export const getAllPages = ({ getMany, query: baseQuery, cacheConfig, limit = 100 }) => (queryParams) => {
    const nonNullQueryParams = queryParams || {};
    const queryWithoutPaginationParams = {
        ...(baseQuery || {}),
        ...nonNullQueryParams
    };
    const queryParamsLimit = 'limit' in nonNullQueryParams ? nonNullQueryParams.limit : undefined;
    let limitFinal = limit;
    if (!isNil(queryParamsLimit) && queryParamsLimit > limit) {
        limitFinal = queryParamsLimit;
    }
    const getManyRequester = getMany({
        ...queryWithoutPaginationParams
    });
    /** The key is composed of the getMany requester key (without pagination query params) and the getAllPages cache key, so that we get a new key on params change */
    const key = [`${getManyRequester.key}${cacheKeys.getAllPages}`];
    const request = async () => {
        /* Page start at 0, order of different query levels matter */
        const getPage = (page) => getMany({
            ...queryWithoutPaginationParams,
            ...getPageQueryParams(page, limitFinal)
        }).request();
        const firstPage = await getPage(0);
        const numberOfRemainingItemsToFetch = firstPage.count - firstPage.results.length;
        if (numberOfRemainingItemsToFetch > 0) {
            /** Ceiling is important, if I have numberOfRemainingItemsToFetch === 2 and limit === 100, length would be 0.02, but we want to fetch one page */
            const numberOfRemainingPagesToFetch = Math.ceil(
            // here we use firstPage.results.length because if limit passed to service is too high backend will override our limit to lower limit
            // if we use limitFinal here we can have the wrong number of page computed
            numberOfRemainingItemsToFetch / firstPage.results.length);
            const nextPages = await Promise.all(
            // We already fetched the 0 page, so start with page 1
            Array.from({ length: numberOfRemainingPagesToFetch }, (_, i) => getPage(i + 1)));
            return firstPage.results.concat(nextPages.reduce((acc, page) => acc.concat(page.results), []));
        }
        else
            return firstPage.results;
    };
    return {
        key,
        request,
        invalidationKey: getManyRequester.invalidationKey,
        cacheConfig,
        fetchAndTransform: () => Promise.reject()
    };
};
// TODO: rename generic types
export const update = ({ rootPath, fromRaw, toRaw, requesterBuilder, params, method = 'PUT', fromError, buildKeysToInvalidate, patternToInvalidate, patternToDelete, ...ctxParams }) => (resourceId) => requesterBuilder({
    path: `${rootPath}${typeof resourceId !== 'undefined' ? `/${resourceId}` : ''}`,
    requestCtx: {
        transform: fromRaw,
        method,
        toRaw,
        params,
        fromError,
        ...ctxParams
    },
    patternToDelete,
    patternToInvalidate,
    keysToInvalidate: buildKeysToInvalidate ? buildKeysToInvalidate() : undefined
});
export const create = ({ rootPath, fromRaw, toRaw, 
// TODO: Why does it not type check ?
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
parse = async (x) => x.json(), requesterBuilder, buildKeysToInvalidate, patternToDelete, patternToInvalidate, ...ctxParams }) => () => requesterBuilder({
    path: rootPath,
    requestCtx: {
        transform: fromRaw,
        method: 'POST',
        parse,
        toRaw,
        ...ctxParams
    },
    keysToInvalidate: buildKeysToInvalidate?.(),
    patternToDelete,
    patternToInvalidate
});
export const deletion = ({ rootPath, requesterBuilder, params, buildKeysToInvalidate, patternToInvalidate, ...ctxParams }) => (resourceId) => requesterBuilder({
    path: `${rootPath}${typeof resourceId !== 'undefined' ? `/${resourceId}` : ''}`,
    requestCtx: {
        params,
        method: 'DELETE',
        // Empty response should not be parsed, so force empty parsing to override json default parsing
        parse: async (x) => x,
        ...ctxParams
    },
    keysToInvalidate: buildKeysToInvalidate ? buildKeysToInvalidate(resourceId) : [],
    patternToDelete: resourceId,
    patternToInvalidate
});
